/**
 * Launch Simulation Page
 * Modern interface to select and launch preconfigured simulations.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import simulationService from '../../services/simulationService';
import useCaseCatalog from '../../hooks/useCaseCatalog';

// SVG Icons for simulation types
const SimulationIcons = {
  drone: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  ),
  cfd: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
    </svg>
  ),
  wing: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12l9-6v12l-9-6z" />
    </svg>
  ),
};

// Legacy workflows: they predate the case catalog and keep their bespoke
// backend wiring, so their cards stay declared here.
//
// Manifest-driven cases are NOT listed here — they are loaded at runtime from
// GET /api/simulations/cases/, which is built from
// MyMDO-DAFoam/cases/<Name>/mymdo.json. Adding a case there makes it show up on
// this page, with its own inputs form, without touching this file.
// Les fiches historiques ne sont plus proposées au client.
//
// Elles ne passent par aucun contrôle, n'ont pas de formulaire d'entrées, pas de
// trace pas à pas, pas de diagnostic et pas de reprise à chaud. Les proposer à
// côté des cases du catalogue revenait à faire choisir entre une version
// éprouvée et une qui ne l'est pas — et quatre d'entre elles doublonnaient
// mach_tutorial_wing, le même case physique.
//
// La liste reste, et ce drapeau la rallume en un mot : les workflows eux-mêmes
// fonctionnent toujours, et les simulations déjà lancées avec eux continuent
// d'afficher leurs résultats.
const FICHES_HISTORIQUES_VISIBLES = false;

const LEGACY_TEMPLATES = [
  {
    id: 'drone_wing_empirical',
    name: 'Drone Wing - Fast Analysis',
    description: 'Quick aerodynamic analysis using empirical methods. Ideal for initial design exploration.',
    workflow: 'drone_wing',
    mode: 'empirical',
    iconType: 'drone',
    duration: '~1-2 min',
    cost: '< $0.01',
    parameters: {
      mach: 0.3,
      alpha_deg: 5.0,
      CL_min: 0.4,
      finesse_min: 15.0,
    },
    features: ['Fast results', 'Low cost', 'Initial exploration'],
  },
  {
    id: 'drone_wing_cfd',
    name: 'Drone Wing - CFD Analysis',
    description: 'High-fidelity CFD simulation for accurate aerodynamic coefficients.',
    workflow: 'drone_wing',
    mode: 'cfd',
    iconType: 'cfd',
    duration: '~20-30 min',
    cost: '$0.05',
    parameters: {
      mach: 0.3,
      alpha_deg: 5.0,
      CL_min: 0.4,
      finesse_min: 15.0,
    },
    features: ['High accuracy', 'CFD solver', 'Detailed results'],
  },
  {
    id: 'mach_wing_quick',
    name: 'MACH Wing - Quick Test',
    description: 'Ultrafast aerostructural optimization for testing. Only 2 iterations for quick validation.',
    workflow: 'mach_wing',
    mode: 'quick',
    iconType: 'wing',
    duration: '~25-40 min',
    cost: '$0.05',
    parameters: {
      optimizer: 'SLSQP',
      max_iterations: 2,
      CL_target: 0.5,
    },
    features: ['Ultrafast', '2 iterations', 'Testing only'],
  },
  {
    id: 'mach_wing_fast',
    name: 'MACH Wing - Fast Optimization',
    description: 'Fast aerostructural optimization with reduced iterations. Good balance of speed and quality.',
    workflow: 'mach_wing',
    mode: 'fast',
    iconType: 'wing',
    duration: '~1-2 hours',
    cost: '$0.20',
    parameters: {
      optimizer: 'SLSQP',
      max_iterations: 10,
      CL_target: 0.5,
    },
    features: ['Fast results', '10 iterations', 'Good balance'],
  },
  {
    id: 'mach_wing_aero_only',
    name: 'MACH Wing - Aero Only',
    description: 'Aerodynamic-only optimization without structural coupling. Faster convergence for aero studies.',
    workflow: 'mach_wing',
    mode: 'aero_only',
    iconType: 'wing',
    duration: '~3-5 hours',
    cost: '$0.80',
    parameters: {
      optimizer: 'SLSQP',
      max_iterations: 15,
      CL_target: 0.5,
    },
    features: ['Aero only', 'No structure', 'Fast convergence'],
  },
  {
    id: 'mach_wing_standard',
    name: 'MACH Wing - Full Optimization',
    description: 'Complete aerostructural optimization using MACH framework. Minimizes drag with full convergence.',
    workflow: 'mach_wing',
    mode: 'standard',
    iconType: 'wing',
    duration: '~6-15 hours',
    cost: '$1.50',
    parameters: {
      optimizer: 'SLSQP',
      max_iterations: 30,
      CL_target: 0.5,
    },
    features: ['Full convergence', '30 iterations', 'Production quality'],
  },
  {
    id: 'dlr_cfd',
    name: 'DLR F6 - CFD Analysis',
    description: 'High-fidelity CFD simulation of the DLR-F6 wing-body configuration. Pre-meshed case with parallel MPI execution.',
    workflow: 'dlr',
    mode: 'cfd',
    iconType: 'cfd',
    duration: '~1-2 hours',
    cost: '$0.15',
    parameters: {},
    features: ['12-core MPI', 'Pre-meshed', 'Production CFD'],
  },
];

/**
 * Choisit la version anglaise d'un libellé quand elle existe et que
 * l'utilisateur l'a demandée.
 *
 * Définie hors du composant à dessein : à l'intérieur, elle serait recréée à
 * chaque rendu et deviendrait une dépendance de chaque useMemo qui l'appelle.
 * Un cas sans traduction garde son libellé — ne pas être traduit ne doit pas
 * empêcher d'être lancé.
 */
const traduire = (defaut, anglais, enAnglais) => (enAnglais && anglais ? anglais : defaut);

/* Un champ du formulaire de lancement.
 *
 * Tout ce qui s'affiche ici vient du manifeste du case : c'est l'équipe
 * simulation qui décide quelle question est posée et comment. La plateforme ne
 * connaît pas la physique — elle rend ce qu'on lui déclare.
 */
const Champ = ({ spec, valeur, onChange, onEtatFichier, enAnglais, workflowMode }) => {
  const id = `param-${spec.name}`;
  const [envoi, setEnvoi] = useState(null);   // null | 0..100
  const [nomFichier, setNomFichier] = useState(null);
  const [erreurFichier, setErreurFichier] = useState(null);

  const choisirFichier = async (fichier) => {
    if (!fichier) return;
    setErreurFichier(null);
    setNomFichier(fichier.name);
    setEnvoi(0);
    // Le parent doit le savoir : tant que l'envoi n'est pas fini, la valeur du
    // champ est vide, et lancer maintenant ferait tourner le calcul sur la
    // géométrie de référence sans que personne ne s'en aperçoive.
    onEtatFichier?.(spec.name, 'envoi');
    try {
      // La valeur du champ n'est PAS le fichier : c'est la clé S3 que le calcul
      // recevra. Le fichier lui-même ne passe jamais par notre backend.
      const cle = await simulationService.envoyerFichier(fichier, {
        workflowMode,
        inputName: spec.name,
        onProgres: setEnvoi,
      });
      onChange(spec, cle);
      setEnvoi(100);
      onEtatFichier?.(spec.name, 'attache');
    } catch (e) {
      // Le serveur nomme le format attendu : le relayer tel quel vaut mieux
      // qu'un message générique, c'est lui qui évite un second essai raté.
      setErreurFichier(e?.response?.data?.message || e.message);
      setEnvoi(null);
      setNomFichier(null);
      onChange(spec, '');
      onEtatFichier?.(spec.name, 'echec');
    }
  };
  const libelle = traduire(spec.label, spec.label_en, enAnglais)
    || spec.name.replace(/_/g, ' ');
  const aide = traduire(spec.help, spec.help_en, enAnglais);
  const estNombre = spec.type !== 'select' && spec.type !== 'boolean'
    && spec.type !== 'file';

  return (
    <div className="bg-dark-900 rounded-lg p-3">
      <label htmlFor={id} className="block text-dark-500 text-xs uppercase tracking-wider mb-2">
        {libelle}
      </label>

      {spec.type === 'file' ? (
        <div>
          <input
            id={id}
            type="file"
            accept={(spec.accept || []).join(',')}
            onChange={(e) => choisirFichier(e.target.files?.[0])}
            className="w-full text-dark-300 text-xs file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:bg-dark-700 file:text-dark-200 file:text-xs hover:file:bg-dark-600"
          />
          {envoi !== null && (
            <div className="mt-2">
              <div className="h-1 bg-dark-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${envoi}%` }}
                />
              </div>
              <p className="text-dark-500 text-xs mt-1 font-mono truncate">
                {envoi === 100 ? `✓ ${nomFichier}` : `${envoi} %`}
              </p>
            </div>
          )}
          {erreurFichier && (
            <p className="text-error-400 text-xs mt-2">{erreurFichier}</p>
          )}
          {(spec.accept || []).length > 0 && envoi === null && !erreurFichier && (
            <p className="text-dark-600 text-xs mt-1 font-mono">
              {(spec.accept || []).join(' · ')}
              {spec.max_mb ? ` — ${spec.max_mb} Mo max` : ''}
            </p>
          )}
        </div>
      ) : spec.type === 'select' ? (
        <select
          id={id}
          value={valeur}
          onChange={(e) => onChange(spec, e.target.value)}
          className="w-full bg-dark-800 border border-dark-700 rounded px-2 py-1.5 text-dark-100 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          {(spec.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : spec.type === 'boolean' ? (
        <input
          id={id}
          type="checkbox"
          checked={Boolean(valeur)}
          onChange={(e) => onChange(spec, e.target.checked)}
          className="h-5 w-5 accent-primary-500"
        />
      ) : (
        <div className="flex items-baseline gap-2">
          <input
            id={id}
            type={spec.type === 'text' ? 'text' : 'number'}
            value={valeur}
            min={spec.min}
            max={spec.max}
            step={spec.type === 'integer' ? 1 : 'any'}
            onChange={(e) => onChange(spec, e.target.value)}
            className="w-full bg-dark-800 border border-dark-700 rounded px-2 py-1.5 text-dark-100 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          {/* L'unité se lit avec la valeur, pas ailleurs : « 285 » ne veut rien
              dire, « 285 m/s » oui. */}
          {spec.unit && (
            <span className="text-dark-400 text-xs font-mono shrink-0">{spec.unit}</span>
          )}
        </div>
      )}

      {aide && <p className="text-dark-400 text-xs mt-2 leading-snug">{aide}</p>}

      {estNombre && (spec.min !== undefined || spec.max !== undefined) && (
        <p className="text-dark-600 text-xs mt-1 font-mono">
          {spec.min !== undefined ? spec.min : '−∞'} … {spec.max !== undefined ? spec.max : '+∞'}
        </p>
      )}
    </div>
  );
};

const LaunchSimulationPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLaunching, setIsLaunching] = useState(false);
  // Où en est chaque champ fichier : 'envoi' | 'attache' | 'echec'. Sans cela le
  // bouton ne voit rien et laisse partir un calcul auquel il manque un fichier.
  const [etatsFichiers, setEtatsFichiers] = useState({});
  const [error, setError] = useState(null);
  const [canLaunch, setCanLaunch] = useState(null); // null = loading
  const [permissionReason, setPermissionReason] = useState(null);
  // Values of the editable inputs of the selected manifest-driven case.
  const [paramValues, setParamValues] = useState({});

  const {
    cases: catalogCases,
    templates: catalogTemplates,
    loading: catalogLoading,
    error: catalogError,
  } = useCaseCatalog();

  // Filtres de la liste. Sur douze fiches réparties en quatre rangées de
  // cartes, rien ne se compare : la durée et le coût tombent à des hauteurs
  // différentes selon la longueur de la description.
  const [recherche, setRecherche] = useState('');
  const [domaine, setDomaine] = useState('tous');
  const [tri, setTri] = useState('defaut');

  // Legacy cards first (stable ordering), then whatever the catalog publishes.
  // Leur libellé vit dans une constante hors composant, donc hors de portée de
  // t : on le traduit ici par identifiant, avec repli sur le texte d'origine.
  const templates = useMemo(() => [
    ...(FICHES_HISTORIQUES_VISIBLES ? LEGACY_TEMPLATES : []).map((l) => ({
      ...l,
      name: i18n.exists(`fiches.${l.id}.nom`) ? t(`fiches.${l.id}.nom`) : l.name,
      description: i18n.exists(`fiches.${l.id}.description`)
        ? t(`fiches.${l.id}.description`)
        : l.description,
    })),
    ...catalogTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      workflow: t.workflow,
      mode: t.mode,
      iconType: SimulationIcons[t.icon] ? t.icon : 'cfd',
      duration: t.duration || '—',
      cost: t.cost || '—',
      parameters: t.parameters || {},
      features: t.features || [],
      // Presence of a schema is what makes a card's parameters editable.
      inputsSchema: t.inputs_schema || [],
      caseId: t.case_id,
    })),
  ], [catalogTemplates, t, i18n]);

  // Choisit la version anglaise quand elle existe et que l'utilisateur l'a
  // demandée. Un cas sans traduction garde son libellé : ne pas être traduit
  // ne doit pas empêcher d'être lancé.
  const enAnglais = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('en');

  // Un cas porte son libellé et son domaine ; ses variantes n'en héritent pas
  // dans la charge utile. On rapproche les deux ici plutôt que d'élargir l'API.
  const infosParCase = useMemo(() => {
    const table = {};
    (catalogCases || []).forEach((c) => {
      table[c.id] = {
        label: traduire(c.label || c.id, c.label_en, enAnglais),
        category: c.category || 'cfd',
      };
    });
    return table;
  }, [catalogCases, enAnglais]);

  // Les fiches viennent de `templates`, qui ne transporte pas les traductions ;
  // les variantes des cas, si. On les rapproche par identifiant.
  const traductionParVariante = useMemo(() => {
    const table = {};
    (catalogCases || []).forEach((c) => {
      (c.variants || []).forEach((v) => {
        table[v.id] = { name_en: v.name_en, description_en: v.description_en };
      });
    });
    return table;
  }, [catalogCases]);

  // Check launch permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      const result = await simulationService.canLaunchSimulations();
      setCanLaunch(result.can_launch);
      setPermissionReason(result.reason);
    };
    checkPermissions();
  }, []);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    // Seed the form with the variant's resolved defaults.
    setParamValues({ ...(template.parameters || {}) });
    setEtatsFichiers({});
    setError(null);
  };

  const handleEtatFichier = (nom, etat) => {
    setEtatsFichiers((prev) => ({ ...prev, [nom]: etat }));
  };

  // Pourquoi on ne peut pas lancer maintenant, ou null si rien ne s'y oppose.
  //
  // Le 12 septembre 2026, un calcul est parti 0,9 s après la fin d'un envoi de
  // 16 Mo : le champ était encore vide, la géométrie du client n'a jamais atteint
  // le calcul, et le résultat est revenu identique à celui de la référence. Rien,
  // nulle part, ne l'a signalé. C'est ce silence-là qu'on ferme ici.
  const etats = Object.values(etatsFichiers);
  const blocage = etats.includes('envoi')
    ? t('lancement.envoiEnCours')
    : etats.includes('echec')
      ? t('lancement.envoiEchoue')
      : null;

  const handleParamChange = (spec, rawValue) => {
    setParamValues((prev) => ({
      ...prev,
      [spec.name]:
        spec.type === 'number' || spec.type === 'integer'
          ? (rawValue === '' ? '' : Number(rawValue))
          : rawValue,
    }));
  };

  const handleLaunch = async () => {
    if (!selectedTemplate) return;

    setIsLaunching(true);
    setError(null);

    const isCatalogCase = (selectedTemplate.inputsSchema || []).length > 0;

    // A hidden input is what the chosen card decides — the task, typically.
    // The backend's precedence is manifest default < variant < submitted value,
    // so sending it back would let the form contradict the card: pick "full
    // optimization" and its 2-4 hours, send task=run_model, get an 8-minute
    // direct run. Dropping it here leaves variant_id to resolve it.
    const nomsCaches = new Set(
      (selectedTemplate.inputsSchema || []).filter((s) => s.hidden).map((s) => s.name)
    );
    const valeursEnvoyees = Object.fromEntries(
      Object.entries(paramValues).filter(([nom]) => !nomsCaches.has(nom))
    );

    try {
      const response = await simulationService.createSimulation({
        workflow_mode: selectedTemplate.workflow,
        analysis_mode: selectedTemplate.mode,
        // variant_id lets the backend fill in any input the form did not send.
        ...(isCatalogCase ? { variant_id: selectedTemplate.id } : {}),
        ...(isCatalogCase ? valeursEnvoyees : selectedTemplate.parameters),
      });

      // Navigate to the detail page
      navigate(`/app/simulations/${response.simulation_id}`);
    } catch (err) {
      // The backend validates inputs against the manifest (bounds, enums), so
      // surface its message rather than a generic failure.
      setError(
        err.response?.data?.message
        || err.response?.data?.workflow_mode?.[0]
        || 'Failed to launch simulation'
      );
      setIsLaunching(false);
    }
  };

  // Domaines proposés au filtre : ceux réellement présents, plus « Autres »
  // pour les workflows historiques qui n'en déclarent aucun.
  const domaines = useMemo(() => {
    const vus = new Set();
    templates.forEach((t) => {
      vus.add(t.caseId ? (infosParCase[t.caseId]?.category || 'cfd') : 'autres');
    });
    return ['tous', ...Array.from(vus).sort()];
  }, [templates, infosParCase]);

  // Un coût comparable pour trier. Les fiches historiques écrivent « $0.15 »,
  // le catalogue expose cost_usd ; sans nombre exploitable on renvoie l'infini
  // pour que la fiche finisse en bas plutôt que de fausser l'ordre.
  const coutNumerique = (t) => {
    if (typeof t.cost_usd === 'number') return t.cost_usd;
    const trouve = String(t.cost || '').match(/[\d.]+/);
    return trouve ? parseFloat(trouve[0]) : Number.POSITIVE_INFINITY;
  };

  const templatesVisibles = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    let liste = templates.map((t) => {
      const tr = traductionParVariante[t.id];
      if (!tr) return t;
      return {
        ...t,
        name: traduire(t.name, tr.name_en, enAnglais),
        description: traduire(t.description, tr.description_en, enAnglais),
      };
    }).filter((t) => {
      const cat = t.caseId ? (infosParCase[t.caseId]?.category || 'cfd') : 'autres';
      if (domaine !== 'tous' && cat !== domaine) return false;
      if (!q) return true;
      return `${t.name} ${t.description}`.toLowerCase().includes(q);
    });
    if (tri === 'cout') liste = [...liste].sort((a, b) => coutNumerique(a) - coutNumerique(b));
    if (tri === 'nom') liste = [...liste].sort((a, b) => a.name.localeCompare(b.name));
    return liste;
  }, [templates, infosParCase, traductionParVariante, enAnglais, recherche, domaine, tri]);

  // Regroupées par cas : on choisit d'abord QUOI simuler, puis jusqu'où. Deux
  // variantes d'un même cas étaient jusqu'ici deux cartes sans lien visible.
  const groupes = useMemo(() => {
    const par = new Map();
    templatesVisibles.forEach((fiche) => {
      const cle = fiche.caseId || '__historique__';
      if (!par.has(cle)) {
        par.set(cle, {
          cle,
          titre: fiche.caseId
            ? (infosParCase[fiche.caseId]?.label || fiche.caseId)
            : t('lancement.workflowsHistoriques'),
          categorie: fiche.caseId ? (infosParCase[fiche.caseId]?.category || 'cfd') : null,
          // L'icône vient de la première fiche du groupe : elle appartient au
          // cas, pas à la variante, et sert de repère visuel dans la liste.
          icone: fiche.iconType,
          fiches: [],
        });
      }
      par.get(cle).fiches.push(fiche);
    });
    // Les cas du catalogue d'abord : ce sont eux qu'on fait évoluer.
    return Array.from(par.values()).sort((a, b) =>
      (a.cle === '__historique__' ? 1 : 0) - (b.cle === '__historique__' ? 1 : 0));
  }, [templatesVisibles, infosParCase, t]);

  // Un input marqué « hidden » n'est pas réglable : il porte ce que la fiche
  // choisie a déjà décidé. L'afficher reviendrait à poser deux fois la même
  // question, avec le droit de se contredire.
  // Un champ « hidden » est supprimé (une variante impose déjà sa valeur) ; un
  // champ « depends_on » n'apparaît que si la question a un sens — demander une
  // portance visée à un calcul qui n'optimise pas n'en a aucun.
  // Échap ferme la fenêtre, et le fond de page cesse de défiler derrière elle.
  // Sans ça, la molette fait glisser la liste sous la fenêtre au lieu de faire
  // défiler le formulaire.
  useEffect(() => {
    if (!selectedTemplate) return undefined;
    const surTouche = (e) => {
      if (e.key === 'Escape') setSelectedTemplate(null);
    };
    const debordement = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', surTouche);
    return () => {
      window.removeEventListener('keydown', surTouche);
      document.body.style.overflow = debordement;
    };
  }, [selectedTemplate]);

  const specsVisibles = (selectedTemplate?.inputsSchema || []).filter((s) => {
    if (s.hidden) return false;
    if (!s.depends_on) return true;
    return Object.entries(s.depends_on).every(([cle, attendu]) => {
      const courant = paramValues[cle];
      // La valeur peut venir du formulaire ou, si l'utilisateur n'y a pas
      // touché, du défaut déclaré — voire de la variante choisie.
      const defaut = (selectedTemplate?.inputsSchema || [])
        .find((x) => x.name === cle)?.default;
      return String(courant ?? defaut ?? '') === String(attendu);
    });
  });

  // « advanced » replie sans supprimer : un réglage d'ingénieur reste
  // accessible sans encombrer la question posée au client.
  const specsCourantes = specsVisibles.filter((s) => !s.advanced);
  const specsAvancees = specsVisibles.filter((s) => s.advanced);

  // Les champs se rangent sous leur titre de section. Ceux qui n'en déclarent
  // pas restent en tête, sans titre : un case simple garde un formulaire simple.
  // Le titre de section se traduit comme le reste. On regroupe sur le libellé
  // FRANÇAIS — la clé stable — et on affiche la version de la langue choisie :
  // deux champs du même groupe doivent se retrouver ensemble quelle que soit la
  // langue.
  const parGroupe = (liste) => {
    const ordre = [];
    const par = new Map();
    liste.forEach((spec) => {
      const cle = spec.group || '';
      if (!par.has(cle)) {
        par.set(cle, { titre: traduire(spec.group, spec.group_en, enAnglais), champs: [] });
        ordre.push(cle);
      }
      par.get(cle).champs.push(spec);
    });
    return ordre.map((cle) => [par.get(cle).titre || '', par.get(cle).champs]);
  };
  const aUnSchema = (selectedTemplate?.inputsSchema || []).length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-dark-100">{t('lancement.titre')}</h1>
        <p className="text-dark-400 mt-1">
          {t('lancement.sousTitre')}
        </p>
      </div>

      {/* Permission Warning Banner */}
      {canLaunch === false && (
        <div className="bg-warning-500/10 border border-warning-500/30 rounded-lg p-4 flex items-start gap-3">
          <span className="text-2xl">🔒</span>
          <div>
            <p className="text-warning-400 font-medium">Launch Disabled</p>
            <p className="text-warning-500 text-sm mt-1">
              {permissionReason || "You don't have permission to launch simulations."}
            </p>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400">{error}</p>
        </div>
      )}

      {/* Catalog degraded: legacy cards still work, manifest cases are missing */}
      {catalogError && (
        <div className="bg-warning-500/10 border border-warning-500/30 rounded-lg p-4">
          <p className="text-warning-400 text-sm">
            Case catalog unavailable — only the built-in workflows are listed.
          </p>
        </div>
      )}

      {/* Filtres — sur douze fiches, chercher vaut mieux que faire défiler */}
      <div className="card-dark p-4 flex flex-col lg:flex-row lg:items-end gap-4">
        <div className="flex-1 min-w-0">
          <label htmlFor="recherche" className="block text-dark-500 text-xs uppercase tracking-wider mb-1">
            {t('lancement.rechercher')}
          </label>
          <input
            id="recherche"
            type="search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder={t('lancement.rechercherPlaceholder')}
            className="w-full bg-dark-800 border border-dark-700 rounded px-3 py-2 text-dark-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="domaine" className="block text-dark-500 text-xs uppercase tracking-wider mb-1">
            {t('lancement.domaine')}
          </label>
          <select
            id="domaine"
            value={domaine}
            onChange={(e) => setDomaine(e.target.value)}
            className="bg-dark-800 border border-dark-700 rounded px-3 py-2 text-dark-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {domaines.map((d) => (
              <option key={d} value={d}>
                {d === 'tous' ? t('lancement.tous') : d === 'autres' ? t('lancement.autres') : d.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="tri" className="block text-dark-500 text-xs uppercase tracking-wider mb-1">
            {t('lancement.trierPar')}
          </label>
          <select
            id="tri"
            value={tri}
            onChange={(e) => setTri(e.target.value)}
            className="bg-dark-800 border border-dark-700 rounded px-3 py-2 text-dark-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="defaut">{t('lancement.triDefaut')}</option>
            <option value="cout">{t('lancement.triCout')}</option>
            <option value="nom">{t('lancement.triNom')}</option>
          </select>
        </div>
        <p className="text-dark-500 text-sm lg:pb-2 whitespace-nowrap">
          {t('lancement.compteur', { visibles: templatesVisibles.length, total: templates.length })}
        </p>
      </div>

      {/* Liste des simulations disponibles.
          De vrais boutons radio plutôt qu'une émulation ARIA : la navigation au
          clavier et l'annonce par un lecteur d'écran viennent alors du
          navigateur, sans qu'on ait à les réimplémenter. */}
      <div role="radiogroup" aria-label="Simulations disponibles" className="space-y-6">
        {groupes.map((groupe) => (
          <div key={groupe.cle}>
            <div className="flex items-center gap-3 mb-2 px-1">
              <span className="text-dark-400" aria-hidden="true">
                {SimulationIcons[groupe.icone] || SimulationIcons.cfd}
              </span>
              <h2 className="text-dark-100 font-semibold">{groupe.titre}</h2>
              {groupe.categorie && (
                <span className="text-dark-500 text-xs">
                  {groupe.categorie.replace(/_/g, ' ')}
                </span>
              )}
            </div>

            <div className="card-dark divide-y divide-dark-700 overflow-hidden">
              {groupe.fiches.map((template) => {
                const choisi = selectedTemplate?.id === template.id;
                return (
                  <label
                    key={template.id}
                    className={`flex flex-col md:flex-row md:items-center gap-3 md:gap-6 p-4 cursor-pointer transition-colors ${
                      choisi ? 'bg-primary-500/10' : 'hover:bg-dark-700/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="simulation"
                      value={template.id}
                      checked={choisi}
                      onChange={() => handleSelectTemplate(template)}
                      className="h-4 w-4 shrink-0 accent-primary-500"
                    />

                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${choisi ? 'text-primary-400' : 'text-dark-100'}`}>
                        {template.name}
                      </p>
                      <p className="text-dark-400 text-sm mt-0.5">{template.description}</p>
                      {template.features.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {template.features.map((f, i) => (
                            <span key={i} className="px-2 py-0.5 bg-dark-800 text-dark-400 text-xs rounded">
                              {f}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Colonnes fixes : c'est ce qui rend deux fiches comparables
                        d'un coup d'œil, ce qu'une grille de cartes interdit. */}
                    <div className="flex items-center gap-6 md:gap-8 shrink-0 md:w-56 md:justify-end">
                      <span className="text-dark-400 text-sm tabular-nums">{template.duration}</span>
                      <span className="text-dark-300 text-sm font-mono tabular-nums">{template.cost}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        {templatesVisibles.length === 0 && !catalogLoading && (
          <div className="card-dark p-8 text-center">
            <p className="text-dark-400">{t('lancement.aucunResultat')}</p>
            <button
              onClick={() => { setRecherche(''); setDomaine('tous'); }}
              className="text-primary-400 text-sm mt-2 hover:underline"
            >
              {t('lancement.toutAfficher')}
            </button>
          </div>
        )}

        {catalogLoading && (
          <div className="card-dark p-4 animate-pulse space-y-3">
            <div className="h-4 bg-dark-700 rounded w-1/3" />
            <div className="h-4 bg-dark-700 rounded w-2/3" />
          </div>
        )}
      </div>

      {/* Les réglages s'ouvrent en fenêtre plutôt qu'en bas de page.
          Un panneau sous une liste de six cases obligeait à faire défiler pour
          voir ce qu'on venait de choisir, puis redéfiler pour lancer — le nom du
          case et le bouton n'étaient jamais visibles ensemble. */}
      {selectedTemplate && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={selectedTemplate.name}
          onClick={() => setSelectedTemplate(null)}
        >
          <div
            className="card-dark w-full max-w-4xl my-auto"
            /* Un clic DANS la fenêtre ne doit pas la fermer. */
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-dark-700 p-6 pb-4">
              <div>
                <h2 className="text-lg font-semibold text-dark-100">
                  {selectedTemplate.name}
                </h2>
                {selectedTemplate.description && (
                  <p className="text-dark-400 text-sm mt-1">{selectedTemplate.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedTemplate(null)}
                aria-label={t('lancement.fermer')}
                className="shrink-0 text-dark-500 hover:text-dark-200 text-2xl leading-none px-2"
              >
                ×
              </button>
            </div>

            <div className="p-6">

          {/* Parameters.
              A manifest-driven case ships an inputs schema (name, label, type,
              bounds, options), so its parameters are rendered as a real form
              and can be tuned before launching. Legacy workflows have no schema
              and keep the read-only summary. */}
          {specsVisibles.length > 0 ? (
            <div className="space-y-5 mb-6">
              {parGroupe(specsCourantes).map(([groupe, champs]) => (
                <div key={groupe || '__sans_groupe__'}>
                  {groupe && (
                    <p className="text-dark-300 text-sm font-medium mb-2">{groupe}</p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {champs.map((spec) => (
                      <Champ
                        key={spec.name}
                        spec={spec}
                        valeur={paramValues[spec.name] ?? ''}
                        onChange={handleParamChange}
                        onEtatFichier={handleEtatFichier}
                        enAnglais={enAnglais}
                        workflowMode={selectedTemplate?.caseId}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* Repliés, pas supprimés : ce sont des réglages d'ingénieur, qui
                  n'ont pas à encombrer la question posée au client. */}
              {specsAvancees.length > 0 && (
                <details className="bg-dark-900/50 rounded-lg">
                  <summary className="cursor-pointer select-none px-3 py-2 text-dark-400 text-sm hover:text-dark-200">
                    {t('lancement.reglagesAvances', { count: specsAvancees.length })}
                  </summary>
                  <div className="p-3 pt-0 space-y-5">
                    {parGroupe(specsAvancees).map(([groupe, champs]) => (
                      <div key={groupe || '__sans_groupe__'}>
                        {groupe && (
                          <p className="text-dark-400 text-xs mb-2">{groupe}</p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {champs.map((spec) => (
                            <Champ
                              key={spec.name}
                              spec={spec}
                              valeur={paramValues[spec.name] ?? ''}
                              onChange={handleParamChange}
                              onEtatFichier={handleEtatFichier}
                              enAnglais={enAnglais}
                              workflowMode={selectedTemplate?.caseId}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : aUnSchema ? (
            <p className="text-dark-400 text-sm mb-6">
              {t('lancement.aucunReglage')}
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {Object.entries(selectedTemplate.parameters).map(([key, value]) => (
                <div key={key} className="bg-dark-900 rounded-lg p-3">
                  <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-dark-100 font-mono">
                    {typeof value === 'number' ? value.toFixed(2) : value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Launch Button */}
          <div className="flex items-center justify-between">
            <p className="text-dark-400 text-sm">
              {t('lancement.dureeEtCout', {
                duree: selectedTemplate.duration,
                cout: selectedTemplate.cost,
              })}
              {blocage && (
                <span className="block text-warning-400 mt-1">{blocage}</span>
              )}
            </p>
            <button
              onClick={handleLaunch}
              disabled={
                isLaunching
                || canLaunch === false
                || canLaunch === null
                || blocage !== null
              }
              title={canLaunch === false ? permissionReason : (blocage || '')}
              className="btn-primary px-8 py-3 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {canLaunch === null ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Checking permissions...
                </>
              ) : isLaunching ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Launching...
                </>
              ) : canLaunch === false ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Launch Disabled
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Launch Simulation
                </>
              )}
            </button>
          </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      {!selectedTemplate && (
        <div className="card-dark p-6 text-center">
          <svg
            className="w-16 h-16 text-dark-600 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          <h3 className="text-lg font-medium text-dark-300 mb-2">
            Select a simulation type
          </h3>
          <p className="text-dark-500">
            Choose one of the preconfigured simulations above to get started
          </p>
        </div>
      )}
    </div>
  );
};

export default LaunchSimulationPage;
